import { createSlice } from '@reduxjs/toolkit';
import { plainAxios, axiosInstance } from '../../../../Common/Interceptors/AxiosConfig';

const initialState = {
  workspaceClass: null,      // Meta info about the vertical
  masterServices: [],        // The final offerings (e.g., Fan Repair)
  serviceGroups: [],         // The groupings (e.g., Electrical)
  activeServiceId: null,      // Selected MasterService (optional deep-link)
  activeGroupId: 'all',        // Selected ServiceGroup (Default to show all)
  currentWorkspaceServices: [], // Individual provider offerings for a master service
  availableMasterServices: [], // Master services not yet enrolled for provider management
  isLoading: false,
  error: null,
  fetchedAtMap: {}, // ✅ TTL cache per id
};

const masterServiceSlice = createSlice({
  name: 'masterServices',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      // Ensure error is always a string or null to prevent React rendering crashes (Error #31)
      state.error = typeof action.payload === 'string'
        ? action.payload
        : (action.payload?.message || "An unexpected error occurred");
      state.isLoading = false;
    },
    setDiscoveryData: (state, action) => {
      state.workspaceClass = action.payload.workspaceClass;
      state.masterServices = action.payload.masterServices || [];
      state.serviceGroups = action.payload.serviceGroups || [];
      state.selectedMasterService = action.payload.selectedMasterService || state.selectedMasterService;
      state.isLoading = false;
      state.error = null;
    },
    setActiveServiceId: (state, action) => {
      state.activeServiceId = action.payload;
    },
    setActiveGroupId: (state, action) => {
      state.activeGroupId = action.payload;
    },
    setWorkspaceServices: (state, action) => {
      const { id, services } = action.payload;
      state.currentWorkspaceServices = services;
      state.fetchedAtMap[id] = Date.now();
      state.isLoading = false;
      state.error = null;
    },
    setAvailableMasterServices: (state, action) => {
      state.availableMasterServices = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    resetServices: (state) => {
      state.masterServices = [];
      state.serviceGroups = [];
      state.activeServiceId = null;
      state.activeGroupId = 'all';
      state.currentWorkspaceServices = [];
      state.workspaceClass = null;
    }
  },
});

export const {
  setLoading,
  setError,
  setDiscoveryData,
  setActiveServiceId,
  setActiveGroupId,
  setWorkspaceServices,
  setAvailableMasterServices,
  resetServices
} = masterServiceSlice.actions;

/**
 * MANUAL THUNK: High-Intent Discovery Fetch
 * Fetches the entire service hierarchy for a Workspace Class
 * with aggregated "Starting From" price metadata.
 */
export const fetchDiscoveryData = (workspaceClassId) => async (dispatch) => {
  if (!workspaceClassId) return;

  dispatch(setLoading(true));
  try {
    // Industrial discovery endpoint for Workspace Class hierarchy
    const response = await plainAxios.get(`discovery/master-services-byclass/${workspaceClassId}`);

    console.log(response, "response masrer")
    if (response.data.success) {
      dispatch(setDiscoveryData(response.data.data));
    } else {
      dispatch(setError(response.data.message));
    }
  } catch (error) {

    dispatch(setError(error.response?.data?.message || 'Failed to initialize discovery hub'));
  }
};

/**
 * THUNK: Fetch Provider-Specific Services
 * Fetches all workspaces offering a specific master service.
 * Enriched with ratings and booking metadata.
 */
export const fetchWorkspaceServices = (masterServiceId, options = {}) => async (dispatch) => {
  const { silent = false } = options;
  if (!masterServiceId) return;

  if (!silent) dispatch(setLoading(true));
  try {
    const response = await plainAxios.get(`discovery/workspace-services/${masterServiceId}`);

    if (response.data.success) {
      const services = response.data.data;
      dispatch(setWorkspaceServices({ id: masterServiceId, services }));

      // ✅ Hydrate Master Service metadata from first result for breadcrumbs
      if (services.length > 0) {
        const first = services[0];
        dispatch(setDiscoveryData({
          workspaceClass: { _id: first.workspaceClassId, name: 'Discovery' },
          masterServices: [],
          serviceGroups: [],
          selectedMasterService: {
            id: first.masterServiceId,
            name: first.name,
            icon: first.icon,
          }
        }));
      }
    } else if (!silent) {
      dispatch(setError(response.data.message));
    }
  } catch (error) {

    if (!silent) dispatch(setError(error.response?.data?.message || 'Failed to load providers'));
  } finally {
    if (!silent) dispatch(setLoading(false));
  }
};

/**
 * THUNK: Fetch available master services for a workspace
 * Used in provider management to see what services can be added.
 */
export const fetchAvailableMasterServices = (workspaceId) => async (dispatch) => {
  if (!workspaceId) return;

  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get(`workspaces/${workspaceId}/available-master-services`);

    if (response.data.success) {
      dispatch(setAvailableMasterServices(response.data.data));
    } else {
      dispatch(setError(response.data.message));
    }
  } catch (error) {
    dispatch(setError(error.response?.data?.message || 'Failed to load available services'));
  } finally {
    dispatch(setLoading(false));
  }
};

export default masterServiceSlice.reducer;
